// Client mirror of functions-common/src/config/planLimits.ts. Keep in sync.
// Mirrors and overrides static configuration from tierConfiguration.json and workspaceLimits.ts.

import tierConfiguration from './tierConfiguration.json'
import {
  WORKSPACE_LIMIT_HARD_CAPS,
  WORKSPACE_COUNT_LIMIT_KEYS,
  type WorkspaceCountLimitKey,
  WORKSPACE_TIER_CAP_KEYS,
  type WorkspaceTierCapKey,
} from './workspaceLimitCaps'

export const TIER_IDS = ['FREE_TIER', 'ESSENTIAL_TIER', 'GROWTH_TIER', 'PROFESSIONAL_TIER', 'ENTERPRISE_TIER'] as const
export type TierId = typeof TIER_IDS[number]

export const LEGACY_TIER_MAP = {
  FREE: 'FREE_TIER', LITE_TIER: 'ESSENTIAL_TIER', SEMI_PRO_TIER: 'GROWTH_TIER', PRO_PLUS_TIER: 'PROFESSIONAL_TIER',
  PRO_TEAM_TIER: 'PROFESSIONAL_TIER', team_workspace_storage_pro: 'PROFESSIONAL_TIER', FULL_PRO_TIER: 'ENTERPRISE_TIER',
} as const satisfies Record<string, TierId>

export const normalizeTierId = (plan: unknown): TierId => {
  if (TIER_IDS.includes(plan as TierId)) return plan as TierId
  return LEGACY_TIER_MAP[String(plan) as keyof typeof LEGACY_TIER_MAP] ?? 'FREE_TIER'
}

export const FEATURE_KEYS = [
  'workspaces',
  'storageGB',
  'chatHistoryDays',
  'chatMessageLimit',
  'activeIntegrations',
  'meetingsPerMonth',
  'webhookCallsPerMonth',
  'auditLogRetentionDays',
  'syncSpeedSeconds',
  'aiChatHistories',
  'aiContextAttachments',
  'maxSavedCharts',
  'whatsappMonthlyCap',
] as const

export const WHATSAPP_MESSAGES_PER_CREDIT = 4


export type FeatureKey = typeof FEATURE_KEYS[number]

export { WORKSPACE_COUNT_LIMIT_KEYS, type WorkspaceCountLimitKey, WORKSPACE_TIER_CAP_KEYS, type WorkspaceTierCapKey }

export type TierLimitOverrides = {
  tierLimits?: Partial<Record<FeatureKey, number>>
  workspaceDefaults?: Partial<Record<WorkspaceCountLimitKey, number>>
  workspaceCaps?: Partial<Record<WorkspaceCountLimitKey, number>>
}

export type PlanLimitOverrides = {
  tiers: Partial<Record<TierId, TierLimitOverrides>>
  version: number
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * Normalizes raw plan limit overrides into a valid PlanLimitOverrides object.
 * Unknown tiers and keys are dropped. Out-of-range values are clamped.
 * Never throws.
 */
export function normalizePlanLimitOverrides(raw: unknown): PlanLimitOverrides {
  if (!isPlainObject(raw)) {
    return { tiers: {}, version: 0 }
  }

  let version = 0
  if (typeof raw.version === 'number' && Number.isFinite(raw.version)) {
    version = Math.max(0, Math.floor(raw.version))
  }

  const tiers: Partial<Record<TierId, TierLimitOverrides>> = {}

  if (isPlainObject(raw.tiers)) {
    for (const tierId of TIER_IDS) {
      const rawTier = raw.tiers[tierId]
      if (!isPlainObject(rawTier)) continue

      const tierEntry: TierLimitOverrides = {}

      // 1. tierLimits
      if (isPlainObject(rawTier.tierLimits)) {
        const normalizedLimits: Partial<Record<FeatureKey, number>> = {}
        for (const key of FEATURE_KEYS) {
          const val = rawTier.tierLimits[key]
          if (typeof val === 'number' && Number.isFinite(val)) {
            if (val === -1) {
              normalizedLimits[key] = -1
            } else if (val >= 0) {
              normalizedLimits[key] = key === 'storageGB' ? val : Math.round(val)
            }
          }
        }
        if (Object.keys(normalizedLimits).length > 0) {
          tierEntry.tierLimits = normalizedLimits
        }
      }

      // 2. workspaceCaps
      let normalizedCaps: Partial<Record<WorkspaceCountLimitKey, number>> | undefined
      if (isPlainObject(rawTier.workspaceCaps)) {
        normalizedCaps = {}
        for (const key of WORKSPACE_COUNT_LIMIT_KEYS) {
          const val = rawTier.workspaceCaps[key]
          if (typeof val === 'number' && Number.isFinite(val)) {
            const hardCap = WORKSPACE_LIMIT_HARD_CAPS[key]
            if (val === -1) {
              normalizedCaps[key] = -1
            } else {
              normalizedCaps[key] = Math.max(1, Math.min(hardCap, Math.round(val)))
            }
          }
        }
      }

      // Migration: convert old tierLimits.goalsPerWorkspace / tasksPerGoal / subtasksPerTask to workspaceCaps
      if (isPlainObject(rawTier.tierLimits)) {
        const legacyMigrationMap: Record<string, WorkspaceCountLimitKey> = {
          goalsPerWorkspace: 'maxGoalsPerBoard',
          tasksPerGoal: 'maxTasksPerGoal',
          subtasksPerTask: 'maxSubtasksPerTask',
        }
        for (const [legacyKey, targetKey] of Object.entries(legacyMigrationMap)) {
          const val = (rawTier.tierLimits as Record<string, unknown>)[legacyKey]
          if (typeof val === 'number' && Number.isFinite(val)) {
            if (!normalizedCaps) normalizedCaps = {}
            if (normalizedCaps[targetKey] === undefined) {
              const hardCap = WORKSPACE_LIMIT_HARD_CAPS[targetKey]
              const targetVal = val === -1 ? -1 : Math.max(1, Math.min(hardCap, Math.round(val)))
              normalizedCaps[targetKey] = targetVal
              console.info(`[planLimits migration] Migrated tierLimits.${legacyKey} (${val}) to workspaceCaps.${targetKey} for tier ${tierId}`)
            }
          }
        }
      }

      if (normalizedCaps && Object.keys(normalizedCaps).length > 0) {
        tierEntry.workspaceCaps = normalizedCaps
      } else {
        normalizedCaps = undefined
      }

      // 3. workspaceDefaults
      if (isPlainObject(rawTier.workspaceDefaults)) {
        const normalizedDefaults: Partial<Record<WorkspaceCountLimitKey, number>> = {}
        for (const key of WORKSPACE_COUNT_LIMIT_KEYS) {
          const val = rawTier.workspaceDefaults[key]
          if (typeof val === 'number' && Number.isFinite(val)) {
            const effectiveCap = normalizedCaps?.[key] ?? WORKSPACE_LIMIT_HARD_CAPS[key]
            if (val === -1) {
              normalizedDefaults[key] = -1
            } else if (effectiveCap === -1) {
              const hardCap = WORKSPACE_LIMIT_HARD_CAPS[key]
              normalizedDefaults[key] = Math.max(1, Math.min(hardCap, Math.round(val)))
            } else {
              normalizedDefaults[key] = Math.max(1, Math.min(effectiveCap, Math.round(val)))
            }
          }
        }
        if (Object.keys(normalizedDefaults).length > 0) {
          tierEntry.workspaceDefaults = normalizedDefaults
        }
      }

      if (tierEntry.tierLimits || tierEntry.workspaceCaps || tierEntry.workspaceDefaults) {
        tiers[tierId] = tierEntry
      }
    }
  }

  return { tiers, version }
}

/**
 * Resolves effective tier limit for a given tier and feature key.
 * Uses override if present, else code default from tierConfiguration.
 */
export function resolveTierLimit(tier: TierId, key: FeatureKey, overrides?: PlanLimitOverrides): number {
  const override = overrides?.tiers?.[tier]?.tierLimits?.[key]
  if (typeof override === 'number' && Number.isFinite(override)) {
    return override
  }
  return tierConfiguration.tiers[tier].limits[key]
}

/**
 * Resolves effective team workspace caps for a tier.
 * Per key min(override cap, hard cap); a missing override falls back to Admin maximum.
 * For WORKSPACE_TIER_CAP_KEYS, code cap comes from tierConfiguration.workspaceCaps, with -1 supported.
 * Mirrors getAdminEditableMaximum (src/utils/workspaceLimits.ts:85-100). Both copies must match.
 */
export function resolveTeamWorkspaceCaps(tier: TierId, overrides?: PlanLimitOverrides): Record<WorkspaceCountLimitKey, number> {
  const result = {} as Record<WorkspaceCountLimitKey, number>
  for (const key of WORKSPACE_COUNT_LIMIT_KEYS) {
    const override = overrides?.tiers?.[tier]?.workspaceCaps?.[key]
    const hardCap = WORKSPACE_LIMIT_HARD_CAPS[key]
    if (typeof override === 'number' && Number.isFinite(override)) {
      if (override === -1) {
        result[key] = -1
      } else {
        result[key] = Math.min(Math.max(1, Math.round(override)), hardCap)
      }
    } else {
      if ((WORKSPACE_TIER_CAP_KEYS as readonly string[]).includes(key)) {
        const codeCap = tierConfiguration.tiers[tier]?.workspaceCaps?.[key as WorkspaceTierCapKey]
        result[key] = codeCap ?? hardCap
      } else if (key === 'maxAiPromptCharacters') {
        result[key] = hardCap
      } else {
        result[key] = Math.max(1, Math.round(hardCap * 0.4))
      }
    }
  }
  return result
}

/**
 * Resolves personal workspace defaults.
 * Follows the owner's tier if provided, or FREE_TIER defaults for tier caps and 0.5 * hardCap for non-tier keys.
 */
export function resolvePersonalWorkspaceDefaults(tier?: TierId, overrides?: PlanLimitOverrides): Record<WorkspaceCountLimitKey, number> {
  if (tier) {
    return resolveTeamWorkspaceDefaults(tier, overrides)
  }
  const freeDefaults = resolveTeamWorkspaceDefaults('FREE_TIER', undefined)
  const result = { ...freeDefaults }
  for (const key of WORKSPACE_COUNT_LIMIT_KEYS) {
    if (!(WORKSPACE_TIER_CAP_KEYS as readonly string[]).includes(key)) {
      result[key] = Math.max(1, Math.round(WORKSPACE_LIMIT_HARD_CAPS[key] * 0.5))
    }
  }
  return result
}

/**
 * Resolves personal workspace caps.
 * Follows the owner's tier if provided, or FREE_TIER caps for tier caps and hard caps for non-tier keys.
 */
export function resolvePersonalWorkspaceCaps(tier?: TierId, overrides?: PlanLimitOverrides): Record<WorkspaceCountLimitKey, number> {
  if (tier) {
    return resolveTeamWorkspaceCaps(tier, overrides)
  }
  const freeCaps = resolveTeamWorkspaceCaps('FREE_TIER', undefined)
  const result = { ...freeCaps }
  for (const key of WORKSPACE_COUNT_LIMIT_KEYS) {
    if (!(WORKSPACE_TIER_CAP_KEYS as readonly string[]).includes(key)) {
      result[key] = WORKSPACE_LIMIT_HARD_CAPS[key]
    }
  }
  return result
}

/**
 * Resolves effective team workspace defaults for a tier.
 * For WORKSPACE_TIER_CAP_KEYS, team default equals the tier cap.
 * Mirrors src/utils/workspaceLimits.ts. Both copies must stay identical.
 */
export function resolveTeamWorkspaceDefaults(tier: TierId, overrides?: PlanLimitOverrides): Record<WorkspaceCountLimitKey, number> {
  const caps = resolveTeamWorkspaceCaps(tier, overrides)
  const result = {} as Record<WorkspaceCountLimitKey, number>

  for (const key of WORKSPACE_COUNT_LIMIT_KEYS) {
    const cap = caps[key]
    const overrideDefault = overrides?.tiers?.[tier]?.workspaceDefaults?.[key]
    const hardCap = WORKSPACE_LIMIT_HARD_CAPS[key]

    if ((WORKSPACE_TIER_CAP_KEYS as readonly string[]).includes(key)) {
      if (typeof overrideDefault === 'number' && Number.isFinite(overrideDefault)) {
        if (overrideDefault === -1) {
          result[key] = cap === -1 ? -1 : cap
        } else if (cap === -1) {
          result[key] = Math.min(Math.max(1, Math.round(overrideDefault)), hardCap)
        } else {
          result[key] = Math.min(Math.max(1, Math.round(overrideDefault)), cap)
        }
      } else {
        result[key] = cap
      }
    } else {
      let codeTeamDefault: number
      if (key === 'maxAiPromptCharacters') {
        codeTeamDefault = 5000
      } else {
        codeTeamDefault = Math.max(1, Math.round(hardCap * 0.25))
      }

      const chosen = (typeof overrideDefault === 'number' && Number.isFinite(overrideDefault))
        ? Math.max(1, Math.round(overrideDefault))
        : codeTeamDefault

      result[key] = Math.min(chosen, cap)
    }
  }

  return result
}

function isDeepEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true
  if (typeof a !== 'object' || a === null || typeof b !== 'object' || b === null) return false
  const keysA = Object.keys(a)
  const keysB = Object.keys(b)
  if (keysA.length !== keysB.length) return false
  for (const key of keysA) {
    if (!Object.prototype.hasOwnProperty.call(b, key)) return false
    if (!isDeepEqual((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])) return false
  }
  return true
}

let storeOverrides: PlanLimitOverrides = { tiers: {}, version: 0 }
const listeners = new Set<() => void>()

export function getPlanLimitOverrides(): PlanLimitOverrides {
  return storeOverrides
}

export function setPlanLimitOverridesForApp(value: PlanLimitOverrides): void {
  if (storeOverrides.version === value.version && isDeepEqual(storeOverrides.tiers, value.tiers)) {
    return
  }
  storeOverrides = value
  listeners.forEach((listener) => {
    try {
      listener()
    } catch (err) {
      console.error('[planLimitsStore] listener threw:', err)
    }
  })
}

export function subscribePlanLimitsStore(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function clearPlanLimitsStoreForTests(): void {
  storeOverrides = { tiers: {}, version: 0 }
  listeners.clear()
}

export function validatePlanLimitOverrides(raw: unknown): { ok: true; value: PlanLimitOverrides } | { ok: false; errors: string[] } {
  const errors: string[] = []

  if (!isPlainObject(raw)) {
    return { ok: false, errors: ['planLimits must be an object'] }
  }

  // Top-level properties check
  for (const prop of Object.keys(raw)) {
    if (prop !== 'tiers' && prop !== 'version') {
      errors.push(`Unknown property '${prop}' on planLimits`)
    }
  }

  // Version check (optional on input, but if provided must be a non-negative integer)
  if ('version' in raw && raw.version !== undefined) {
    if (typeof raw.version !== 'number' || !Number.isInteger(raw.version) || raw.version < 0) {
      errors.push('version must be a non-negative integer')
    }
  }

  // Tiers check
  if ('tiers' in raw && raw.tiers !== undefined) {
    if (!isPlainObject(raw.tiers)) {
      errors.push('tiers must be an object')
    } else {
      const allowedTiers = new Set<string>(TIER_IDS)
      for (const tierKey of Object.keys(raw.tiers)) {
        if (!allowedTiers.has(tierKey)) {
          errors.push(`Unknown tier '${tierKey}'`)
          continue
        }

        const tierObj = raw.tiers[tierKey]
        if (!isPlainObject(tierObj)) {
          errors.push(`Tier '${tierKey}' must be an object`)
          continue
        }

        for (const sec of Object.keys(tierObj)) {
          if (sec !== 'tierLimits' && sec !== 'workspaceCaps' && sec !== 'workspaceDefaults') {
            errors.push(`Unknown section '${sec}' in tier '${tierKey}'`)
          }
        }

        // Section: tierLimits
        if ('tierLimits' in tierObj && tierObj.tierLimits !== undefined) {
          if (!isPlainObject(tierObj.tierLimits)) {
            errors.push(`tierLimits in tier '${tierKey}' must be an object`)
          } else {
            const allowedFeatureKeys = new Set<string>(FEATURE_KEYS)
            const legacyFeatureKeys = new Set<string>(['goalsPerWorkspace', 'tasksPerGoal', 'subtasksPerTask'])
            for (const featKey of Object.keys(tierObj.tierLimits)) {
              if (!allowedFeatureKeys.has(featKey) && !legacyFeatureKeys.has(featKey)) {
                errors.push(`Unknown tierLimit feature key '${featKey}' in tier '${tierKey}'`)
                continue
              }
              const val = tierObj.tierLimits[featKey]
              if (typeof val !== 'number' || !Number.isFinite(val)) {
                errors.push(`tierLimits.${featKey} in tier '${tierKey}' must be a finite number`)
              } else if (val !== -1 && val < 0) {
                errors.push(`tierLimits.${featKey} in tier '${tierKey}' must be -1 or >= 0`)
              } else if (featKey !== 'storageGB' && !Number.isInteger(val)) {
                errors.push(`tierLimits.${featKey} in tier '${tierKey}' must be an integer`)
              }
            }
          }
        }

        // Section: workspaceCaps
        const validatedCaps: Partial<Record<WorkspaceCountLimitKey, number>> = {}
        if ('workspaceCaps' in tierObj && tierObj.workspaceCaps !== undefined) {
          if (!isPlainObject(tierObj.workspaceCaps)) {
            errors.push(`workspaceCaps in tier '${tierKey}' must be an object`)
          } else {
            const allowedCapKeys = new Set<string>(WORKSPACE_COUNT_LIMIT_KEYS)
            for (const capKey of Object.keys(tierObj.workspaceCaps)) {
              if (!allowedCapKeys.has(capKey)) {
                errors.push(`Unknown workspaceCaps key '${capKey}' in tier '${tierKey}'`)
                continue
              }
              const key = capKey as WorkspaceCountLimitKey
              const val = tierObj.workspaceCaps[capKey]
              const hardCap = WORKSPACE_LIMIT_HARD_CAPS[key]
              if (typeof val !== 'number' || !Number.isInteger(val) || (val !== -1 && (val < 1 || val > hardCap))) {
                errors.push(`workspaceCaps.${capKey} in tier '${tierKey}' must be -1 or an integer between 1 and ${hardCap}`)
              } else {
                validatedCaps[key] = val
              }
            }
          }
        }

        // Section: workspaceDefaults
        if ('workspaceDefaults' in tierObj && tierObj.workspaceDefaults !== undefined) {
          if (!isPlainObject(tierObj.workspaceDefaults)) {
            errors.push(`workspaceDefaults in tier '${tierKey}' must be an object`)
          } else {
            const allowedDefKeys = new Set<string>(WORKSPACE_COUNT_LIMIT_KEYS)
            for (const defKey of Object.keys(tierObj.workspaceDefaults)) {
              if (!allowedDefKeys.has(defKey)) {
                errors.push(`Unknown workspaceDefaults key '${defKey}' in tier '${tierKey}'`)
                continue
              }
              const key = defKey as WorkspaceCountLimitKey
              const effectiveCap = validatedCaps[key] ?? resolveTeamWorkspaceCaps(tierKey as TierId)[key]
              const val = tierObj.workspaceDefaults[defKey]
              if (effectiveCap === -1) {
                const hardCap = WORKSPACE_LIMIT_HARD_CAPS[key]
                if (typeof val !== 'number' || !Number.isInteger(val) || (val !== -1 && (val < 1 || val > hardCap))) {
                  errors.push(`workspaceDefaults.${defKey} in tier '${tierKey}' must be -1 or an integer between 1 and ${hardCap}`)
                }
              } else {
                if (typeof val !== 'number' || !Number.isInteger(val) || val < 1 || val > effectiveCap) {
                  errors.push(`workspaceDefaults.${defKey} in tier '${tierKey}' must be an integer between 1 and ${effectiveCap}`)
                }
              }
            }
          }
        }
      }
    }
  }

  if (errors.length > 0) {
    return { ok: false, errors }
  }

  return { ok: true, value: normalizePlanLimitOverrides(raw) }
}
